import React from 'react';
import _ from 'lodash';

import {
  Button as MuiButton,
  IconButton,
  Link as MuiLink,
  Skeleton,
  styled,
  Typography
} from '@mui/material';
import {
  NetSuiteRecordChip,
  routeDirectory,
  tableUtil,
  tier1
} from 'modules';
import {
  modalUtil,
  Table
} from 'iron-forge-react-modules';

import { ChevronRight } from '@mui/icons-material';
import * as dataService from './dataService';
import * as types from './types';



const Button = styled(MuiButton)(() => ({
  '&:hover': {
    backgroundColor: '#E5EFFD'
  },
  backgroundColor: '#E5EFFD',
  color: '#3A3B3D'
}));



const Link = styled(MuiLink)({
  fontSize: 12
});

const TableComponent = Table.genTableComponent<types.IColumnId>({
  tableHeaderCellStyle: {
    textAlign: 'center'
  },
  tableBodyCellStyle: {
    border: 'none'
  }
});



export interface IProps {
  columnIdsToRender: types.IColumnId[];
  filter: types.IFilter;
}

export const FC: React.FC<IProps> = props => {
  const {
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['qualityControl']);
  const {
    queryTuple,
    fnQueryPage,
    fnQuery,
    pageInfo
  } = dataService.useQuery(props.filter);
  const lastFilter = React.useRef(props.filter);



  React.useEffect(
    () => {
      if (!_.isEqual(props.filter, lastFilter.current)) {
        fnQuery({ filter: props.filter });
        lastFilter.current = props.filter;
      }
    },
    [fnQuery, props.filter]
  );



  const handleClickPrintLabel = React.useCallback(
    (labelUrl: string) => {
      window.open(labelUrl, '_blank');
    },
    []
  );

  const loadedRows = React.useMemo(
    () => _.map(queryTuple.data.returnAuthorizations, (ra): types.IRow => ({
      id: ra.id,
      columnId2Value: {
        client: {
          type: 'component',
          sortableValue: ra.clientName,
          label: (
            <Link
              href={''}
              onClick={() => alert('Takes user to NetSuite record for customer')}>
              {ra.clientName}
            </Link>)
        },
        itemReceiptDate: {
          type: 'string',
          sortableValue: ra.itemReceiptDate?.toMillis() ?? 0,
          label: ra.itemReceiptDate?.toFormat('M/d/yy') ?? '-'
        },
        nsReceiptRecord: {
          type: 'component',
          sortableValue: ra.referenceNumber,
          label: <NetSuiteRecordChip.FC writeStatus={ra.itemReceiptWriteStatus} />
        },
        nsRmaRecord: {
          type: 'component',
          sortableValue: ra.referenceNumber,
          label: <NetSuiteRecordChip.FC writeStatus={ra.returnAuthorizationWriteStatus} />
        },
        observedIssue: {
          type: 'string',
          label: ra.observedIssue
        },
        productName: {
          type: 'string',
          label: ra.productName
        },
        productSku: {
          type: 'string',
          label: ra.productSku
        },
        referenceNumber: {
          type: 'component',
          sortableValue: ra.referenceNumber,
          label: (
            <Link
              target="_blank"
              href={routeDirectory.admin.tier1ReturnAuthorizations.detail.path(ra.id)}>
              {ra.referenceNumber}
            </Link>
          )
        },
        requestedDate: {
          type: 'string',
          label: ra.requestDate.toFormat('M/d/yy')
        },
        serialNumber: {
          type: 'string',
          label: ra.serialNumber
        },
        shippingLabel: {
          type: 'component',
          sortableValue: '',
          label: (
            ra.shippingLabelUrl ? (
              <Button onClick={_.partial(handleClickPrintLabel, ra.shippingLabelUrl)}>
                Print Label
              </Button>
            ) : (
              <Typography>-</Typography>
            )
          )
        },
        qualityControl: {
          type: 'component',
          sortableValue: '',
          label: (
            <Button
              variant="contained"
              disableElevation
              onClick={() => setModalIsOpen('qualityControl', ra.id)}>
              Quality Control
            </Button>
          )
        },
        goTo: {
          type: 'component',
          sortableValue: '',
          label: (
            <IconButton
              size="small"
              href={routeDirectory.admin.tier1ReturnAuthorizations.detail.path(ra.id)}
              target="_blank">
              <ChevronRight fontSize="small" />
            </IconButton>
          )
        }
      }
    })),
    [
      handleClickPrintLabel,
      setModalIsOpen,
      queryTuple.data.returnAuthorizations
    ]
  );

  const loadingRows = React.useMemo(
    () => _.times(50).map((i): types.IRow => ({
      id: i.toString(),
      columnId2Value: {
        client: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        itemReceiptDate: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        nsReceiptRecord: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        nsRmaRecord: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        observedIssue: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        productName: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        productSku: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        referenceNumber: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        requestedDate: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        serialNumber: {
          type: 'component',
          sortableValue: i,
          label: <Skeleton />
        },
        shippingLabel: {
          type: 'component',
          sortableValue: '',
          label: <Skeleton />
        },
        qualityControl: {
          type: 'component',
          sortableValue: '',
          label: <Skeleton />
        },
        goTo: {
          type: 'component',
          sortableValue: '',
          label: <Skeleton />
        }
      }
    })),
    []
  );

  const columns = tableUtil.useSortedColumns({
    columnIdsToRender: props.columnIdsToRender,
    columns: __columns
  });

  return (
    <>
      <TableComponent
        rows={queryTuple.loading ? loadingRows : loadedRows}
        columns={columns}
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
        onChangeSortOptions={options => fnQuery({ sort: options })}
        initSortOptions={{
          columnId: 'requestedDate',
          direction: 'descending'
        }} />

      {modalType2TargetId.qualityControl && (
        <tier1.QualityControlModal.FC
          storeId={props.filter.storeId ?? ''}
          returnAuthorizationId={modalType2TargetId.qualityControl}
          onClose={() => {
            setModalIsOpen('qualityControl', false);
            queryTuple.refetch?.();
          }} />
      )}
    </>
  );
};



const __columns: Table.IColumn<types.IColumnId>[] = [
  {
    id: 'client',
    label: "Customer"
  },
  {
    id: 'itemReceiptDate',
    label: "Received Date"
  },
  {
    id: 'nsReceiptRecord',
    label: "Item Receipt"
  },
  {
    id: 'nsRmaRecord',
    label: "Q1 RMA Number"
  },
  {
    id: 'observedIssue',
    label: "Observed Issue"
  },
  {
    id: 'productName',
    label: "Product Model"
  },
  {
    id: 'productSku',
    label: "Product SKU"
  },
  {
    id: 'referenceNumber',
    label: "Customer Reference Number"
  },
  {
    id: 'requestedDate',
    label: 'Pre-Alert Date'
  },
  {
    id: 'serialNumber',
    label: "IMEI"
  },
  {
    id: 'shippingLabel',
    label: "Shipping Label"
  },
  {
    id: 'qualityControl',
    label: ""
  },
  {
    id: 'goTo',
    label: ""
  }
];
